import { createClient } from "@supabase/supabase-js";
import { trackAuthAttempt } from './analytics';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Update client options to persist session
export const supabase = createClient(supabaseUrl, supabasePublishableKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

// Add error messages for different auth scenarios
export const AuthErrors = {
  NO_SESSION: "No active session found",
  NOT_AUTHORIZED: "User is not authorized",
  INVALID_USER: "Invalid user credentials",
  UNKNOWN: "An unknown error occurred",
} as const;

const isProd = import.meta.env.PROD;

export const isSecureEnvironment = () => {
  if (isProd) {
    // Check for secure connection
    if (window.location.protocol !== 'https:') {
      throw new Error('Production requires HTTPS');
    }
  }
};

export const signInWithGitHub = async () => {
  try {
    // Track the attempt
    await trackAuthAttempt(false);
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "github",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
        skipBrowserRedirect: true, // Prevent automatic redirect
      },
    });

    if (error) throw error;

    if (data?.url) {
      // Store the return path before redirecting
      sessionStorage.setItem("authReturnPath", window.location.pathname);
      window.location.href = data.url;
    }

    return { data, error: null };
  } catch (error) {
    // console.error("Auth error:", error);
    return {
      data: null,
      error: error instanceof Error ? error.message : AuthErrors.UNKNOWN,
    };
  }
};

export const handleAuthCallback = async () => {
  try {
    const { error } = await supabase.auth.getSession();
    if (error) throw error;

    // Track successful auth (no user identifiers are recorded)
    await trackAuthAttempt(true, "github");

    const returnPath = sessionStorage.getItem("authReturnPath") || "/blog";
    sessionStorage.removeItem("authReturnPath");

    return { error: null, returnPath };
  } catch (error) {
    // Track failed auth
    await trackAuthAttempt(false, "github");

    return {
      error: error instanceof Error ? error.message : AuthErrors.UNKNOWN,
      returnPath: "/blog",
    };
  }
};

export async function signOut() {
  return await supabase.auth.signOut();
}

export async function isAuthorizedUser(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      // console.log('No active session');
      return false;
    }

    // Check user's role - will be 'viewer' by default thanks to the trigger
    const { data: userRole, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
      .maybeSingle();

    if (error) {
      // console.error('Error checking role:', error);
      return false;
    }

    // console.log('User authorization check:', {
    //   userId: session.user.id,
    //   role: userRole?.role,
    //   isAdmin: userRole?.role === 'admin'
    // });

    return userRole?.role === 'admin';
  } catch {
    // console.error('Auth error:', error);
    return false;
  }
}

// Add this helper function
export async function getCurrentUserRole(): Promise<'admin' | 'viewer' | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: userRole } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user?.id)
      .single();

    return userRole?.role || null;
  } catch {
    // console.error('Error getting user role:', error);
    return null;
  }
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

export interface SiteSettings {
  maintenance_mode: boolean;
  blog_enabled: boolean;
}

export async function getSiteSettings(): Promise<SiteSettings> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('*')
    .single();

  if (error) {
    // console.error('Error fetching site settings:', error);
    return { maintenance_mode: false, blog_enabled: true };
  }

  return data;
}

export async function updateSiteSettings(
  patch: Partial<Pick<SiteSettings, 'maintenance_mode' | 'blog_enabled'>>
) {
  const { error } = await supabase
    .from('site_settings')
    .update(patch)
    .eq('id', 1);

  if (error) throw error;
  return { success: true };
}

export async function updateMaintenanceMode(enabled: boolean) {
  return updateSiteSettings({ maintenance_mode: enabled });
}
